import { useMutation } from '@tanstack/react-query';

import { ChangeTeamSettingEventAdapter } from '~analytics/adapters/ChangeTeamSettingEventAdapter';
import { queryClient } from '~teacher/queryClient';

import { changeTeamSettings } from '../../../api/teams';
import { Team, TeamSettings, teamSettingsToTeamSettingNames } from '../../../api/teams/getTeams';
import { teamsKeys } from './teamsKeys';

type Context = {
  allTeams: Array<Team>;
  originalSettings: TeamSettings;
};

export function useChangeTeamSettings() {
  return useMutation({
    mutationFn: changeTeamSettings,
    onMutate: async ({ teamId, canInvite, canViewEmail }) => {
      await queryClient.cancelQueries({ queryKey: teamsKeys.byId(teamId) });

      const allTeams = queryClient.getQueryData<Array<Team>>(teamsKeys.all);

      const originalSettings = allTeams?.find(
        (team) => team.id === teamId
      )?.settings;

      const newTeams = allTeams?.map((team) =>
        team.id === teamId
          ? {
              ...team,
              settings: {
                ...team.settings,
                ...(canInvite && { canInvite }),
                ...(canViewEmail && { canViewEmail }),
              },
            }
          : team
      );

      queryClient.setQueryData(teamsKeys.all, newTeams);

      return { allTeams, originalSettings };
    },
    onSuccess: (savedSettings, variables, { originalSettings }: Context) => {
      new ChangeTeamSettingEventAdapter({
        teamId: variables.teamId,
        original_settings: teamSettingsToTeamSettingNames(originalSettings),
        saved_settings: teamSettingsToTeamSettingNames(savedSettings),
      }).sendAnalytics();

      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (error, variables, { allTeams }: Context) => {
      if (allTeams) {
        // Rollback the optimistic update on errors
        queryClient.setQueryData(teamsKeys.all, allTeams);
      }
    },
  });
}
